import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useApi } from "/src/helpers/Hooks/useApi";
import formatMoney from "/src/helpers/formatMoney";
import Button from "/src/helpers/components/button.jsx";
import PickupMap, { walkLabel } from "/src/helpers/components/pickupMap.jsx";
import { rateBreakdown } from "/src/helpers/excursionRates";
import ImageGallery from "/src/helpers/components/imageGallery.jsx";
import { track, ecommerce, excursionItem } from "/src/helpers/analytics";
import {
    PinIcon, ClockIcon, CartIcon, CheckIcon, GlobeIcon, CalendarIcon, CameraIcon, ChevronRightIcon,
} from "/src/helpers/components/upsellIcons.jsx";

const gbp = (v) => `£${formatMoney(v)}`;
// Local-date key — never via toISOString(), which shifts BST midnights back a day.
const dayKey = (d) => {
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
const dayParts = (iso) => {
    const d = new Date(`${iso}T00:00:00`);
    return {
        dow: d.toLocaleDateString("en-GB", { weekday: "short" }),
        dom: d.getDate(),
        mon: d.toLocaleDateString("en-GB", { month: "short" }),
    };
};
const fmtShort = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

// Every calendar day across the destination stay windows (arrival/departure already excluded
// server-side), tagged with the destination the guest is in that day.
const buildHolidayDays = (destinations) => {
    const days = [];
    (destinations || []).forEach((dest) => {
        for (let d = new Date(dest.start); d <= new Date(dest.end); d.setDate(d.getDate() + 1)) {
            days.push({ date: dayKey(d), destination: dest.name, propertyReferenceId: dest.propertyReferenceId });
        }
    });
    return days;
};

// The API returns one row per bookable date; the shop groups rows into one card per excursion
// per base destination, with its dated options collected.
const groupExcursions = (rows) => {
    const map = new Map();
    (rows || []).forEach((r) => {
        const key = `${r.extraId}-${r.basePropertyReferenceId}`;
        if (!map.has(key)) {
            map.set(key, { key, ...r, options: [] });
        }
        map.get(key).options.push({
            optionToken: r.optionToken, date: dayKey(r.availableFrom),
            startTime: r.startTime, endTime: r.endTime,
            price: r.price, perPersonPrice: r.perPersonPrice
        });
    });
    return [...map.values()];
};

// iVector itinerary text arrives as plain lines; "- item" / "* item" lines become bullets.
const DetailText = ({ text }) => {
    const lines = (text || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const bullets = lines.filter((l) => /^[-*•]\s*/.test(l));
    if (lines.length > 0 && bullets.length === lines.length) {
        return (
            <ul className="list-disc ml-5 flex flex-col gap-1">
                {lines.map((l, i) => <li key={i}>{l.replace(/^[-*•]\s*/, "")}</li>)}
            </ul>
        );
    }
    return <p className="whitespace-pre-line">{lines.join("\n")}</p>;
};

// "More details" panel: CMS summary + the iVector itinerary sections (Highlights, Itinerary,
// Includes, Excludes, Joining Instructions) in a two-column grid on desktop.
const DetailsPanel = ({ excursion, bare = false }) => {
    const sections = excursion.details || [];
    const gallery = bare ? [] : (excursion.imageUrls || []).slice(1, 5);
    return (
        <div className={bare ? "" : "px-5 md:px-6 py-5 border-t border-content_5 bg-bg_2/60"}>
            {excursion.summary && <p className="text-[0.875rem] text-primary font-medium mb-4">{excursion.summary}</p>}
            {gallery.length > 0 && (
                <div className="flex gap-2 mb-4 overflow-x-auto">
                    {gallery.map((src) => (
                        <img key={src} src={src} alt="" loading="lazy" className="h-[5.25rem] w-[7.5rem] object-cover rounded-lg shrink-0" />
                    ))}
                </div>
            )}
            {sections.length === 0 && !excursion.summary && (
                <p className="text-[0.84375rem] text-content_2">{excursion.description || "Full details are confirmed on your voucher."}</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                {sections.map((s) => (
                    <div key={s.title} className={s.title === "Itinerary" ? "md:col-span-2" : ""}>
                        <div className="text-[0.6875rem] font-bold text-content_3 uppercase tracking-[0.06em] mb-1.5">{s.title}</div>
                        <div className="text-[0.84375rem] text-content_2 leading-relaxed"><DetailText text={s.text} /></div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Every photo for the gallery: the CMS main image first, then the additional images.
// imageUrls already starts with the main image (server order); imageUrl is only a fallback for rows
// without a gallery. (Combining both duplicated the first slide, as the two carry different resize widths.)
const galleryImages = (excursion) =>
    (excursion.imageUrls || []).length > 0 ? excursion.imageUrls : [excursion.imageUrl].filter(Boolean);

const pickupLabel = (p) => (p ? `${p.location}${p.time ? `, ${p.time}` : ""}` : "");
// "Alamis Apartments · 09:00 · 12 min walk" (or "· 31 min by car" once it's too far to walk) — the
// distance is only known when the pick-up isn't the customer's own hotel (server rule).
const pickupOptionLabel = (p) => {
    const walk = walkLabel(p);
    return `${p.location}${p.time ? ` · ${p.time}` : ""}${walk ? ` · ${walk}` : ""}`;
};

// Shown whenever the excursion doesn't call at the customer's own hotel (wording per Olympic).
const NoHotelPickupNote = ({ className = "" }) => (
    <p className={`text-[0.78125rem] text-content_2 leading-snug ${className}`}>
        Please select from one of the excursion pick-up points below.
    </p>
);

// Pick-up point chooser: a single dropdown (location · time · walk from the hotel) — compact,
// the same control the expanded card uses. Disabled when the pick-up is locked to the hotel.
const PickupPointSelect = ({ points, selectedId, locked, onSelect, className = "" }) => {
    const selected = points.find((p) => p.pickupPointId === selectedId) || (points.length === 1 ? points[0] : null);
    return (
        <select value={selected?.pickupPointId ?? ""} disabled={locked || !onSelect}
                onChange={(e) => onSelect?.(Number(e.target.value))}
                className={`w-full px-3 py-2 rounded-lg border bg-white text-sm cursor-pointer disabled:cursor-default disabled:bg-bg_2
                    ${selected ? "border-content_5 text-primary" : "border-emp text-content_2"} ${className}`}>
            {!selected && <option value="" disabled>Select your pick-up point…</option>}
            {points.map((p) => (
                <option key={p.pickupPointId} value={p.pickupPointId}>{pickupOptionLabel(p)}</option>
            ))}
        </select>
    );
};

// "More details" modal: hero image, description, itinerary sections, every pick-up point, and a
// "Choose a day" CTA that closes the modal and opens the card's day picker.
const ExcursionDetailsModal = ({ excursion, selectedPickupId, onSelectPickup, onClose, onChooseDay, hotel, hotelName, party }) => {
    const rates = rateBreakdown(excursion, party);
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    const points = excursion.pickupPoints || [];
    const locked = !!excursion.pickupLocked;
    const selected = (locked ? points.find((p) => p.pickupPointId === excursion.defaultPickupPointId) : null)
        || points.find((p) => p.pickupPointId === selectedPickupId) || (points.length === 1 ? points[0] : null);
    return (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-emp_deep/50 p-0 md:p-6"
             onClick={onClose} role="dialog" aria-modal="true" aria-label={excursion.name}>
            <div className="bg-white rounded-t-card md:rounded-card shadow-card w-full max-w-3xl max-h-[92vh] overflow-y-auto"
                 onClick={(e) => e.stopPropagation()}>
                <ImageGallery images={galleryImages(excursion)} alt={excursion.name} lazy={false}
                              className="w-full h-48 md:h-64">
                    <button onClick={onClose} aria-label="Close"
                            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 text-primary text-2xl leading-none flex items-center justify-center shadow cursor-pointer hover:bg-white">×</button>
                    {(excursion.tags || []).length > 0 && (
                        <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
                            {excursion.tags.slice(0, 3).map((t) => (
                                <span key={t} className="bg-white/95 text-primary px-2.5 py-1 rounded-full text-xs font-semibold">{t}</span>
                            ))}
                        </div>
                    )}
                </ImageGallery>
                <div className="px-5 md:px-7 py-5">
                    <h2 className="text-2xl font-poppins font-bold text-primary leading-tight">{excursion.name}</h2>
                    <div className="flex gap-[1.375rem] flex-wrap text-[0.8125rem] text-content_2 mt-2">
                        {excursion.durationHours > 0 && (
                            <span className="inline-flex items-center gap-[0.4375rem]"><span className="text-emp_dark"><ClockIcon /></span>{excursion.durationHours} hours</span>
                        )}
                        <span className="inline-flex items-center gap-[0.4375rem]"><span className="text-emp_dark"><PinIcon /></span>Runs from {excursion.baseDestination}</span>
                    </div>
                    {excursion.description && <p className="mt-3 text-[0.875rem] text-content_2 leading-relaxed">{excursion.description}</p>}
                    <div className="mt-4">
                        <DetailsPanel excursion={excursion} bare />
                    </div>
                    {points.length > 0 && (
                        <div className="mt-5">
                            <div className="text-[0.6875rem] font-bold text-content_3 uppercase tracking-[0.06em] mb-2">
                                Pick-up {points.length === 1 ? "point" : `points (${points.length})`}
                            </div>
                            {locked ? (
                                <p className="text-[0.78125rem] text-[#157a54] font-semibold mb-2">
                                    You'll be picked up from your hotel — {selected?.location}{selected?.time ? ` at ${selected.time}` : ""}.
                                </p>
                            ) : points.length > 1 && (
                                <NoHotelPickupNote hotelName={hotelName || hotel?.name} className="mb-2" />
                            )}
                            <PickupPointSelect points={points} selectedId={selected?.pickupPointId} locked={locked}
                                               onSelect={(id) => onSelectPickup?.(id)} />
                            {!locked && (points.some((p) => p.latitude != null) || hotel) && (
                                <PickupMap points={points} selectedId={selected?.pickupPointId} hotel={hotel} className="mt-3" height={220}
                                           onSelect={(id) => onSelectPickup?.(id)} />
                            )}
                        </div>
                    )}
                </div>
                <div className="sticky bottom-0 bg-white border-t border-content_5 px-5 md:px-7 py-4 flex items-center justify-between gap-4">
                    <div>
                        {rates ? (
                            <><span className="text-[0.6875rem] text-content_3 uppercase tracking-[0.05em]">from </span>
                            <span className="text-xl font-bold text-primary">{gbp(rates.headline)}</span>
                            <span className="text-sm text-content_3"> {rates.headlineSuffix}</span>
                            {rates.others.length > 0 && (
                                <div className="text-xs text-content_2">
                                    {rates.others.map((o) => `${o.label} ${o.free ? "free" : gbp(o.price)}`).join(" · ")}
                                </div>
                            )}</>
                        ) : (
                            <span className="text-xl font-bold text-primary">{gbp(excursion.price)}</span>
                        )}
                    </div>
                    <Button variant="primary" fullWidthMobile={false} onClick={onChooseDay}>Choose a day</Button>
                </div>
            </div>
        </div>
    );
};

// Pick-up point: a choice when the excursion offers more than one, a label otherwise; plus an
// embedded map (maps.astrondo.com tiles) when iVector holds coordinates for the point(s).
const PickupChooser = ({ excursion, value, onChange, hotel, hotelName }) => {
    const points = excursion.pickupPoints || [];
    if (points.length === 0) return null;
    const locked = !!excursion.pickupLocked;
    const selected = (locked ? points.find((p) => p.pickupPointId === excursion.defaultPickupPointId) : null)
        || points.find((p) => p.pickupPointId === value) || (points.length === 1 ? points[0] : null);
    const mappable = points.some((p) => p.latitude != null && p.longitude != null) || !!hotel;
    return (
        <div className="bg-bg_2 border border-content_4 rounded-[0.625rem] px-2.5 pt-2.5 pb-[0.6875rem]">
            <div className="text-[0.6875rem] font-semibold text-content_2 uppercase tracking-[0.05em] mb-2">Pick-up point</div>
            {!locked && points.length > 1 ? (
                <>
                    <NoHotelPickupNote hotelName={hotelName || hotel?.name} className="mb-2" />
                    <PickupPointSelect points={points} selectedId={selected?.pickupPointId} onSelect={onChange} />
                </>
            ) : locked ? (
                <div className="flex items-start gap-[0.4375rem] text-sm text-primary">
                    <span className="text-emp_dark mt-0.5"><PinIcon /></span>
                    <span>
                        We'll pick you up from your hotel{selected?.time ? <> at <span className="font-semibold">{selected.time}</span></> : ""}.
                        {selected?.location && <span className="text-content_2"> ({selected.location})</span>}
                    </span>
                </div>
            ) : (
                <div className="flex items-center gap-2 flex-wrap text-sm text-primary font-medium">
                    <span className="inline-flex items-center gap-[0.4375rem]">
                        <span className="text-emp_dark"><PinIcon /></span>
                        {pickupLabel(selected)}
                    </span>
                </div>
            )}
            {/* The map is for finding a pick-up that ISN'T your hotel — always shown in that case,
                never when you're collected from your own hotel. */}
            {!locked && mappable && (
                <>
                    <PickupMap points={points} selectedId={selected?.pickupPointId} hotel={hotel} onSelect={onChange} className="mt-2" />
                    {selected?.mapUrl && (
                        <a href={selected.mapUrl} target="_blank" rel="noopener noreferrer"
                           className="inline-block mt-1.5 text-[0.75rem] text-content_3 hover:underline">Open in Google Maps ↗</a>
                    )}
                </>
            )}
        </div>
    );
};

// Calendar-style date card for the availability rail (prototype DateCard + CountPill).
const DateCard = ({ allDays, date, dest, count, disabled, active, onClick }) => {
    const p = date ? dayParts(date) : null;
    return (
        <button onClick={onClick} disabled={disabled}
                className={`shrink-0 flex flex-col items-center gap-0.5 pt-2.5 pb-2 px-1.5 rounded-xl transition-all cursor-pointer
                    ${allDays ? "w-[4.75rem]" : "w-[3.875rem]"}
                    ${active ? "border-2 border-emp bg-emp text-white"
                        : disabled ? "border-[1.5px] border-content_4 bg-bg_2 text-content_3 opacity-55 cursor-not-allowed"
                        : "border-[1.5px] border-content_4 bg-white text-primary hover:border-emp"}`}>
            {allDays ? (
                <>
                    <span className="mt-0.5"><CalendarIcon size={20} /></span>
                    <span className="text-[0.8125rem] font-bold mt-1">All days</span>
                </>
            ) : (
                <>
                    <span className={`text-[0.65625rem] font-bold uppercase tracking-[0.04em] ${active ? "opacity-85" : "opacity-60"}`}>{p.dow}</span>
                    <span className="text-xl font-bold leading-none">{p.dom}</span>
                    <span className={`text-[0.625rem] ${active ? "opacity-85" : "opacity-55"}`}>{p.mon}</span>
                    {dest && (
                        <span className={`text-[0.53125rem] font-bold uppercase tracking-[0.05em] mt-px ${active ? "text-white/90" : "text-emp_dark"}`}>
                            {dest}
                        </span>
                    )}
                </>
            )}
            {disabled
                ? <span className="text-[0.625rem] mt-1 text-content_3">—</span>
                : <span className={`mt-1 text-[0.65625rem] font-bold leading-none px-[0.4375rem] py-[0.1875rem] rounded-full
                    ${active ? "bg-white/25 text-white" : "bg-emp_soft text-emp_dark"}`}>{count}</span>}
        </button>
    );
};

// Compact calendar day picker inside the expanded row (prototype DayPicker): blue = picked,
// green + ✓ = already in the basket for that day.
const DayPicker = ({ holidayDays, excursion, value, booked, bookedOtherDays, onChange }) => (
    <div className="bg-bg_2 border border-content_4 rounded-[0.625rem] px-2.5 pt-2.5 pb-[0.6875rem]">
        <div className="text-[0.6875rem] font-semibold text-content_2 uppercase tracking-[0.05em] mb-2">
            Pick a day during your stay
        </div>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(3.25rem,1fr))] gap-[0.3125rem]">
            {holidayDays.map((day) => {
                const p = dayParts(day.date);
                const available = excursion.options.some((o) => o.date === day.date);
                const isBooked = booked.has(day.date);
                // A day already taken by a DIFFERENT booked excursion (one excursion per day).
                const takenByOther = !isBooked && bookedOtherDays?.has(day.date);
                const isSel = value === day.date;
                const otherDestination = day.propertyReferenceId !== excursion.basePropertyReferenceId;
                const disabled = (!available && !isBooked) || takenByOther;
                const tooltip = isBooked ? "Already booked"
                    : takenByOther ? "You already have an excursion booked on this day."
                    : !available
                        ? (otherDestination
                            ? `You're in ${day.destination} this day — this excursion runs from ${excursion.baseDestination}.`
                            : "Not available on this day.")
                        : undefined;
                return (
                    <button key={day.date} disabled={disabled} title={tooltip}
                            onClick={() => onChange(day.date)}
                            className={`relative px-0.5 pt-[0.3125rem] pb-1 rounded-[0.4375rem] leading-[1.1] text-center transition-colors
                                ${isBooked ? "border-2 border-green bg-[#e7f6ef] text-[#157a54]"
                                    : isSel ? "border-2 border-emp bg-emp_soft text-primary"
                                    : disabled ? "border border-[#d8e0ea] bg-[#eef1f5] text-content_3 opacity-55 cursor-not-allowed"
                                    : "border border-[#d8e0ea] bg-white text-primary hover:border-emp cursor-pointer"}`}>
                        <div className="text-[0.5625rem] font-semibold uppercase tracking-[0.04em] opacity-65">{p.dow}</div>
                        <div className="text-[0.9375rem] font-bold">{p.dom}</div>
                        <div className="text-[0.53125rem] opacity-60">{p.mon}</div>
                        {isBooked && (
                            <span className="absolute -top-[0.4375rem] -right-1.5 w-[0.9375rem] h-[0.9375rem] rounded-full bg-green text-white
                                             flex items-center justify-center shadow-sm">
                                <CheckIcon size={9} />
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    </div>
);

// Data (eligibility + search rows) is owned by the home page, which fetches it in the background
// on load so the banner can show a from-price and this section opens instantly.
const ExcursionShop = ({ eligibility, rows, basket, onBasketChange, onToast }) => {
    const api = useApi();
    const navigate = useNavigate();
    const railRef = useRef(null);

    const [dayFilter, setDayFilter] = useState(null); // yyyy-mm-dd from the availability rail
    const [openPicker, setOpenPicker] = useState(null); // excursion key
    const [detailsFor, setDetailsFor] = useState(null); // excursion (object) shown in the details modal
    const [pickedDay, setPickedDay] = useState({});     // key -> yyyy-mm-dd
    const [pickedPickup, setPickedPickup] = useState({}); // key -> pickupPointId
    const [swapPrompt, setSwapPrompt] = useState(null); // { key, pendingRequest, message, checkoutAfter }
    const [error, setError] = useState("");

    // Close the day-picker modal and forget the day / pick-up chosen in it — reopening starts fresh.
    const closePicker = () => {
        if (openPicker) {
            setPickedDay(({ [openPicker]: _day, ...rest }) => rest);
            setPickedPickup(({ [openPicker]: _pickup, ...rest }) => rest);
        }
        setOpenPicker(null);
        setSwapPrompt(null);
    };

    // The day-picker modal closes on Escape (the X and the backdrop are handled inline).
    useEffect(() => {
        if (!openPicker) return undefined;
        const onKeyDown = (e) => { if (e.key === "Escape") closePicker(); };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [openPicker]);

    const excursions = useMemo(() => groupExcursions(rows), [rows]);
    // Funnel step 1: the list the customer was shown (once per search result set).
    useEffect(() => {
        if (excursions.length) ecommerce.viewItemList("excursions", "Excursions", excursions.map((e) => excursionItem(e)));
    }, [excursions]);
    const holidayDays = useMemo(() => buildHolidayDays(eligibility?.destinations), [eligibility]);
    // The customer's hotel for an excursion's base destination (map marker), when iVector has coordinates.
    const hotelNameFor = (excursion) =>
        (eligibility?.destinations || []).find((x) => x.propertyReferenceId === excursion.basePropertyReferenceId)?.hotelName || null;
    const hotelFor = (excursion) => {
        const d = (eligibility?.destinations || []).find((x) => x.propertyReferenceId === excursion.basePropertyReferenceId);
        return d && d.hotelLatitude != null && d.hotelLongitude != null
            ? { name: d.hotelName, latitude: d.hotelLatitude, longitude: d.hotelLongitude } : null;
    };

    const railDays = useMemo(() => holidayDays.map((day) => ({
        ...day,
        count: excursions.filter((e) => e.options.some((o) => o.date === day.date)).length
    })), [holidayDays, excursions]);

    const visibleCount = useMemo(
        () => excursions.filter((e) => !dayFilter || e.options.some((o) => o.date === dayFilter)).length,
        [excursions, dayFilter]);

    const byDestination = useMemo(() => {
        const groups = new Map();
        (eligibility?.destinations || []).forEach((d) => groups.set(d.propertyReferenceId, { destination: d, excursions: [] }));
        excursions
            .filter((e) => !dayFilter || e.options.some((o) => o.date === dayFilter))
            .forEach((e) => groups.get(e.basePropertyReferenceId)?.excursions.push(e));
        return [...groups.values()].filter((g) => g.excursions.length > 0);
    }, [eligibility, excursions, dayFilter]);

    const basketCount = (basket?.excursions?.length || 0) + (basket?.car ? 1 : 0);

    // Days this excursion is in the basket for (unpaid, editable) — the green "In basket" state.
    const basketDaysFor = (excursion) => new Set(
        (basket?.excursions || [])
            .filter((l) => l.extraId === excursion.extraId && l.basePropertyReferenceId === excursion.basePropertyReferenceId)
            .map((l) => dayKey(l.useDate)));

    // Days this excursion is already BOOKED and paid for (on the main or linked ancillary booking) —
    // the card is shown as "Booked" and can't be opened for editing.
    const purchasedDaysFor = (excursion) => new Set(
        (eligibility?.bookedExtras || [])
            .filter((b) => b.extraId === excursion.extraId)
            .map((b) => dayKey(b.date)));

    // The party on the booking: child/infant rates are only quoted for types actually travelling.
    const party = { adults: eligibility?.adults || 0, children: eligibility?.children || 0, infants: eligibility?.infants || 0 };

    // Every day with ANY booked excursion — other excursions can't be booked on those days.
    const bookedDates = useMemo(() => new Set(
        (eligibility?.bookedExtras || []).map((b) => dayKey(b.date))), [eligibility]);

    const scrollRail = (dir) => railRef.current?.scrollBy({ left: dir * 320, behavior: "smooth" });

    const addToBasket = async (excursion, checkoutAfter, replaceSameDay = false, requestOverride = null) => {
        setError("");
        const date = pickedDay[excursion.key] || dayFilter; // the rail's day counts as picked
        const option = excursion.options.find((o) => o.date === date);
        if (!option) { setError("Choose a day first."); return; }

        const request = requestOverride || {
            extraId: excursion.extraId,
            optionToken: option.optionToken,
            useDate: date,
            basePropertyReferenceId: excursion.basePropertyReferenceId,
            pickupPointId: pickedPickup[excursion.key] ?? excursion.defaultPickupPointId ?? null,
            replaceSameDay
        };
        const res = await api.basketAddExcursion(request);
        if (res.ok) {
            ecommerce.addToCart(excursionItem(excursion, { item_variant: date }), option.price);
            track("excursion_added", { excursion_id: excursion.extraId, use_date: date, checkout_after: !!checkoutAfter,
                pickup_point_id: request.pickupPointId ?? null, replaced_same_day: !!replaceSameDay });
            onBasketChange(res.data);
            setSwapPrompt(null);
            if (checkoutAfter) { navigate("/checkout"); return; }
            closePicker(); // close the picker so the selected state is visible
            onToast?.(replaceSameDay ? "Excursion swapped" : "Added to basket");
            return;
        }
        if (res.status === 409 && res.data?.title === "Day already taken") {
            setSwapPrompt({ key: excursion.key, message: res.data.detail, checkoutAfter,
                pendingRequest: { ...request, replaceSameDay: true } });
            return;
        }
        setError(res.data?.detail || "We couldn't add that excursion. Please try again.");
    };

    if (rows === null) {
        return <div className="bg-white rounded-card shadow-card p-6 text-content_2">Finding excursions for your holiday…</div>;
    }
    if (!eligibility?.canAdd) {
        return <div className="bg-white rounded-card shadow-card p-6 text-content_2">{eligibility?.reason || "Excursions aren't available for this booking."}</div>;
    }
    if (excursions.length === 0) {
        return <div className="bg-white rounded-card shadow-card p-6 text-content_2">There are no excursions available for your travel dates just yet — check back nearer your holiday.</div>;
    }

    const destinationNames = (eligibility?.destinations || []).map((d) => d.name);
    const heading = destinationNames.length > 0
        ? `Top picks for your ${destinationNames.join(" & ")} trip`
        : "Top picks for your trip";

    return (
        <section className="bg-white rounded-card shadow-card px-4 md:px-8 pt-7 pb-8">
            {/* ── Section header: kicker + heading + basket pill (prototype §3) ──────────── */}
            <div className="flex items-end justify-between flex-wrap gap-3 mb-[1.375rem]">
                <div>
                    <div className="flex items-center gap-3 text-content_2 font-semibold text-[0.8125rem] tracking-[0.06em] uppercase">
                        <span className="text-emp"><GlobeIcon size={22} /></span>
                        Excursions &amp; activities
                    </div>
                    <h2 className="mt-2.5 text-[1.375rem] md:text-[1.75rem] font-poppins font-bold text-primary">{heading}</h2>
                </div>
                {basketCount > 0 && (
                    <button onClick={() => navigate("/checkout")}
                            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-emp hover:bg-emp_dark text-white font-semibold text-[0.84375rem] cursor-pointer">
                        <CartIcon size={16} />
                        Basket · {basketCount}
                    </button>
                )}
            </div>

            {error && <div className="bg-error_2 text-error text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

            {/* ── Availability rail (prototype DateRail) ─────────────────────────────────── */}
            <div className="mb-6 pb-[1.375rem] border-b border-content_5">
                <div className="flex items-center justify-between mb-3">
                    <span className="inline-flex items-center gap-[0.4375rem] text-xs font-bold text-content_3 uppercase tracking-[0.06em]">
                        <CalendarIcon /> Availability
                    </span>
                    <span className="text-[0.84375rem] text-content_2">
                        {dayFilter
                            ? <>Showing <strong className="text-primary">{visibleCount}</strong> for this day</>
                            : <><strong className="text-primary">{excursions.length}</strong> excursion{excursions.length !== 1 ? "s" : ""} across your stay</>}
                    </span>
                </div>
                <div className="flex items-center gap-2.5">
                    <button onClick={() => scrollRail(-1)} aria-label="Earlier dates"
                            className="hidden sm:flex shrink-0 w-[2.125rem] h-[2.125rem] rounded-full bg-white border border-content_5 text-lg text-primary shadow-[0_2px_0.5rem_rgba(8,53,92,0.12)] items-center justify-center cursor-pointer">
                        ‹
                    </button>
                    <div ref={railRef} className="flex gap-2 overflow-x-auto px-0.5 pt-0.5 pb-2 flex-1" role="listbox" aria-label="Filter by day">
                        <DateCard allDays active={dayFilter === null} count={excursions.length} onClick={() => setDayFilter(null)} />
                        {railDays.map((day) => (
                            <DateCard key={day.date} date={day.date} dest={day.destination}
                                      count={day.count} disabled={day.count === 0}
                                      active={dayFilter === day.date}
                                      onClick={() => { if (day.count > 0) { const next = dayFilter === day.date ? null : day.date; track("excursion_day_filter", { date: next }); setDayFilter(next); } }} />
                        ))}
                    </div>
                    <button onClick={() => scrollRail(1)} aria-label="Later dates"
                            className="hidden sm:flex shrink-0 w-[2.125rem] h-[2.125rem] rounded-full bg-white border border-content_5 text-lg text-primary shadow-[0_2px_0.5rem_rgba(8,53,92,0.12)] items-center justify-center cursor-pointer">
                        ›
                    </button>
                </div>
            </div>

            {byDestination.length === 0 && (
                <div className="py-10 text-center text-content_2">
                    No excursions run on that day.{" "}
                    <button onClick={() => setDayFilter(null)} className="text-emp_dark font-semibold cursor-pointer hover:underline">
                        Show all days
                    </button>
                </div>
            )}

            {/* ── Destination groups (prototype ActivitiesList) ──────────────────────────── */}
            <div className="flex flex-col gap-3.5">
                {byDestination.map(({ destination, excursions: group }) => (
                    <React.Fragment key={destination.propertyReferenceId}>
                        <div className="flex items-center gap-2.5 mt-1.5 min-w-0">
                            <span className="inline-flex items-center gap-[0.4375rem] text-emp_dark font-bold text-[0.90625rem] shrink-0">
                                <PinIcon size={14} /> In {destination.name}
                            </span>
                            <span className="text-[0.8125rem] text-content_3 truncate">· your stay {fmtShort(destination.start)} – {fmtShort(destination.end)}</span>
                            <span className="text-xs font-bold bg-emp_soft text-emp_dark px-[0.5625rem] py-0.5 rounded-full shrink-0">{group.length}</span>
                            <span className="flex-1 h-px bg-content_5" />
                        </div>

                        {group.map((excursion) => {
                            const rates = rateBreakdown(excursion, party);
                            const basketDays = basketDaysFor(excursion);
                            const purchasedDays = purchasedDaysFor(excursion);
                            // Booked wins: a paid-for excursion is never presented as an editable basket line.
                            const isBooked = purchasedDays.size > 0;
                            const isSelected = !isBooked && basketDays.size > 0;
                            // Union for the day picker so both basket and booked days read "Already booked".
                            const booked = new Set([...basketDays, ...purchasedDays]);
                            const bookedDayLabel = purchasedDays.size === 1
                                ? (() => { const p = dayParts([...purchasedDays][0]); return `${p.dow} ${p.dom} ${p.mon}`; })()
                                : null;
                            const isOpen = openPicker === excursion.key && !isBooked;
                            // A day picked on the availability rail IS the day — no second picker.
                            const chosen = pickedDay[excursion.key] || dayFilter || null;
                            const pts = excursion.pickupPoints || [];
                            const needsPickup = !excursion.pickupLocked && pts.length > 1
                                && !pts.some((p) => p.pickupPointId === (pickedPickup[excursion.key] ?? excursion.defaultPickupPointId));
                            return (
                                <div key={excursion.key}>
                                <div className={`bg-white rounded-[0.875rem] overflow-hidden
                                        ${(isSelected || isBooked) ? "border-[1.5px] border-green shadow-[0_0_0_0.1875rem_rgba(31,158,110,0.12)]" : "border border-content_5"}`}>
                                    <div className="flex flex-col md:flex-row md:items-stretch">
                                        {/* image (iVector CMS) + overlay tags (CMS overlay text / product attributes) */}
                                        <ImageGallery images={galleryImages(excursion)} alt={excursion.name}
                                                      className="w-full aspect-video md:w-[17.5rem] md:aspect-[4/3] md:shrink-0">
                                            {(excursion.tags || []).length > 0 && (
                                                <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
                                                    {excursion.tags.slice(0, 2).map((t) => (
                                                        <span key={t} className="bg-white/95 text-primary px-2.5 py-1 rounded-full text-xs font-semibold">{t}</span>
                                                    ))}
                                                </div>
                                            )}
                                            {(isSelected || isBooked) && (
                                                <span className="absolute top-3 right-3 inline-flex items-center gap-[0.3125rem] bg-green text-white px-2.5 py-1 rounded-full text-xs font-bold shadow">
                                                    <CheckIcon size={13} /> {isBooked ? "Booked" : "In basket"}
                                                </span>
                                            )}
                                        </ImageGallery>

                                        {/* content */}
                                        <div className="px-5 py-5 md:px-6 flex flex-col flex-1 min-w-0">
                                            <h3 className="text-xl font-poppins font-bold text-primary leading-tight">{excursion.name}</h3>
                                            {excursion.description && (
                                                <p className="mt-2.5 mb-2 text-[0.84375rem] text-content_2 leading-normal line-clamp-2">{excursion.description}</p>
                                            )}
                                            <button onClick={() => { ecommerce.viewItem(excursionItem(excursion)); setDetailsFor(excursion); }}
                                                    className="self-start inline-flex items-center gap-1 mb-3 text-[0.8125rem] font-semibold text-emp_dark hover:underline cursor-pointer">
                                                More details
                                                <ChevronRightIcon size={14} />
                                            </button>
                                            <div className="mt-auto flex gap-[1.375rem] flex-wrap text-[0.8125rem] text-content_2">
                                                {excursion.durationHours > 0 && (
                                                    <span className="inline-flex items-center gap-[0.4375rem]">
                                                        <span className="text-emp_dark"><ClockIcon /></span>
                                                        {excursion.durationHours} hours
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* price column */}
                                        <div className="border-t md:border-t-0 md:border-l border-content_5 bg-bg_2 px-[1.375rem] py-5
                                                        flex flex-row md:flex-col items-center md:items-end justify-between md:justify-center gap-2 md:w-[13.75rem] md:shrink-0">
                                            <div className="text-left md:text-right">
                                                {rates ? (
                                                    <>
                                                        <div className="text-[0.6875rem] text-content_3 uppercase tracking-[0.05em]">from</div>
                                                        <div className="text-[1.875rem] font-bold text-primary leading-none">
                                                            {gbp(rates.headline)}<span className="text-sm font-semibold text-content_3"> {rates.headlineSuffix}</span>
                                                        </div>
                                                        {rates.others.length > 0 && (
                                                            <div className="text-xs text-content_2 mt-1">
                                                                {rates.others.map((o) => `${o.label} ${o.free ? "free" : gbp(o.price)}`).join(" · ")}
                                                            </div>
                                                        )}
                                                        <div className="text-xs text-content_3 mt-1">{gbp(excursion.price)} total for your party</div>
                                                    </>
                                                ) : (
                                                    <div className="text-[1.875rem] font-bold text-primary leading-none">{gbp(excursion.price)}</div>
                                                )}
                                            </div>
                                            {isBooked ? (
                                                <span className="md:mt-1.5 md:w-full px-[1.125rem] py-[0.6875rem] rounded-btn font-semibold text-sm inline-flex items-center justify-center gap-[0.4375rem]
                                                                 bg-[#e7f6ef] text-[#157a54] border-[1.5px] border-green">
                                                    <CheckIcon size={15} /> Booked{bookedDayLabel ? ` · ${bookedDayLabel}` : ""}
                                                </span>
                                            ) : (
                                                <button onClick={() => { if (isOpen) { closePicker(); return; } ecommerce.selectItem("excursions", "Excursions", excursionItem(excursion)); setOpenPicker(excursion.key); setSwapPrompt(null); }}
                                                        className={`md:mt-1.5 md:w-full px-[1.125rem] py-[0.6875rem] rounded-btn font-semibold text-sm inline-flex items-center justify-center gap-[0.4375rem] cursor-pointer transition-colors
                                                            ${isOpen ? "bg-background text-emp_dark"
                                                                : isSelected ? "bg-[#e7f6ef] text-[#157a54] border-[1.5px] border-green"
                                                                : "bg-emp hover:bg-emp_dark text-white"}`}>
                                                    {isOpen ? "Close" : isSelected ? <><CheckIcon size={15} /> Selected · edit</> : dayFilter ? "Select" : "Choose a day"}
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                </div>

                                    {/* day picker + pick-up point + actions in a modal (same chrome as the details
                                        dialog): bottom sheet on mobile, centred on desktop, blurred backdrop,
                                        close on the X / backdrop / Escape. */}
                                    {isOpen && (
                                        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-emp_deep/50 backdrop-blur-sm p-0 md:p-6"
                                             onMouseDown={(e) => { if (e.target === e.currentTarget) closePicker(); }}
                                             role="dialog" aria-modal="true" aria-label={`Choose a day: ${excursion.name}`}>
                                        <div className="w-full md:max-w-[61.25rem] max-h-[92vh] overflow-y-auto bg-white rounded-t-[0.875rem] md:rounded-[0.875rem]
                                                        shadow-[0_1.5rem_3.75rem_rgba(8,53,92,0.35)] px-5 md:px-6 pb-5">
                                            <div className="flex items-start justify-between gap-4 pt-5 pb-3 border-b border-content_5">
                                                <div className="min-w-0">
                                                    <h3 className="text-lg md:text-xl font-poppins font-bold text-primary leading-tight">{excursion.name}</h3>
                                                    <p className="text-sm text-content_2 mt-0.5">Choose a day{pts.length > 0 ? " and pick-up point" : ""} for your party.</p>
                                                </div>
                                                <button type="button" aria-label="Close" onClick={closePicker}
                                                        className="shrink-0 -mr-2 -mt-1 p-2 rounded-lg text-content_2 hover:text-primary hover:bg-bg_2 transition cursor-pointer">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                            <div className="pt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-[1.125rem] items-end">
                                                <div className="flex flex-col gap-3">
                                                    {dayFilter ? (
                                                        <div className="inline-flex items-center gap-[0.4375rem] text-sm text-primary">
                                                            <span className="text-emp_dark"><CalendarIcon size={15} /></span>
                                                            <span className="text-content_3">Day:</span>
                                                            <span className="font-medium">{(() => { const p = dayParts(dayFilter); return `${p.dow} ${p.dom} ${p.mon}`; })()}</span>
                                                        </div>
                                                    ) : (
                                                        <DayPicker holidayDays={holidayDays} excursion={excursion}
                                                                   value={chosen} booked={booked} bookedOtherDays={bookedDates}
                                                                   onChange={(date) => { track("excursion_day_selected", { excursion_id: excursion.extraId, use_date: date }); setPickedDay({ ...pickedDay, [excursion.key]: date }); }} />
                                                    )}
                                                    {/* Always shown, so the customer sees where (and when) they're collected before picking a day. */}
                                                    <PickupChooser excursion={excursion} hotel={hotelFor(excursion)} hotelName={hotelNameFor(excursion)}
                                                                   value={pickedPickup[excursion.key] ?? excursion.defaultPickupPointId}
                                                                   onChange={(id) => { track("pickup_point_selected", { excursion_id: excursion.extraId, pickup_point_id: id }); setPickedPickup({ ...pickedPickup, [excursion.key]: id }); }} />
                                                </div>
                                                <div className="md:mb-3.5">
                                                    {swapPrompt?.key === excursion.key ? (
                                                        <div className="bg-orange_light rounded-lg px-4 py-3 text-sm text-primary flex flex-col gap-3 md:max-w-[17.5rem]">
                                                            <span>{swapPrompt.message}</span>
                                                            <div className="flex gap-2">
                                                                <Button variant="primary" fullWidthMobile={false} className="!py-2 !px-4 text-sm"
                                                                    onClick={() => addToBasket(excursion, swapPrompt.checkoutAfter, true, swapPrompt.pendingRequest)}>
                                                                    Replace
                                                                </Button>
                                                                <Button variant="secondary" fullWidthMobile={false} className="!py-2 !px-4 text-sm"
                                                                    onClick={() => setSwapPrompt(null)}>
                                                                    Cancel
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col gap-2 w-full md:w-auto">
                                                            {(() => {
                                                                const alreadyIn = chosen && booked.has(chosen);
                                                                const disabled = !chosen || needsPickup;
                                                                return (
                                                                    <>
                                                                        <button disabled={disabled || alreadyIn}
                                                                                onClick={() => addToBasket(excursion, false)}
                                                                                className={`inline-flex items-center justify-center gap-[0.4375rem] px-[1.125rem] py-[0.6875rem] rounded-btn font-semibold text-sm whitespace-nowrap w-full
                                                                                    ${alreadyIn ? "bg-green text-white border-[1.5px] border-green"
                                                                                        : "bg-white text-emp_dark border-[1.5px] border-content_4 hover:bg-emp_soft"}
                                                                                    ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                                                                            {alreadyIn ? <><CheckIcon size={15} /> Selected</> : <><CartIcon size={16} strokeWidth={2} /> Add to basket</>}
                                                                        </button>
                                                                        <button disabled={disabled}
                                                                                onClick={() => addToBasket(excursion, true)}
                                                                                className={`inline-flex items-center justify-center gap-[0.4375rem] px-[1.125rem] py-[0.6875rem] rounded-btn font-semibold text-sm whitespace-nowrap w-full
                                                                                    bg-emp hover:bg-emp_dark text-white
                                                                                    ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                                                                            Add &amp; checkout{excursion.price ? ` · ${gbp(excursion.price)}` : ""}
                                                                        </button>
                                                                    </>
                                                                );
                                                            })()}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>

            {detailsFor && (
                <ExcursionDetailsModal
                    excursion={detailsFor} party={party}
                    hotel={hotelFor(detailsFor)} hotelName={hotelNameFor(detailsFor)}
                    selectedPickupId={pickedPickup[detailsFor.key] ?? detailsFor.defaultPickupPointId}
                    onSelectPickup={(id) => setPickedPickup({ ...pickedPickup, [detailsFor.key]: id })}
                    onClose={() => setDetailsFor(null)}
                    onChooseDay={() => { setOpenPicker(detailsFor.key); setSwapPrompt(null); setDetailsFor(null); }}
                />
            )}
        </section>
    );
};

export default ExcursionShop;
